import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useSession } from '@/lib/auth';
import { useActiveLocationId } from '@/lib/locationContext';
import { useFeature } from '@/lib/useFeature';
import Signature from '@/pages/Signature';
import { useHasSpotlight } from '@/lib/useHasSpotlight';
import { QrCodeCard } from '@/components/QrCodeCard';
import { ProDesigner } from '@/components/ProDesigner';
import { ProLibrary } from '@/components/ProLibrary';
import { Link, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { viewRpcArgs, viewAsProId } from '@/lib/viewAs';
import { WingTabs } from '@/components/WingTabs';
import CheckMyListing from './CheckMyListing';
import { mediaUrl } from '@/lib/hosts';
import { isCell } from '@/lib/cell/config';
import { cellGet, viewQuery } from '@/lib/cell/api';
import { CELL_DOOR_OFF, ownerHubOrigin, ownerHubToken } from '@/lib/cell/ownerHub';

// CELL (M28): the Owner Hub's origin for this build — production's OWNER_HUB_ORIGIN (lib/hosts.ts), or the AWS staging
// host on the cell build; null there = not on the AWS staging build yet, and each door below says so (CELL_DOOR_OFF).
const OWNER_HUB_ORIGIN = ownerHubOrigin();

/**
 * GROW — the pro's marketing room (Peter, 2026-08-27; open item 87210412,
 * mockup dev.cadenceadvisers.com/design/pro-hub-grow.html).
 *
 * The Pro Hub was entirely obligation and facility — nothing in it helped a
 * pro get a client. This room is where that changes, shipped BEFORE the
 * IMAGE Pro app launch so it exists when the pilot conversations happen.
 *
 * Two residents today, matching Peter's "build 1 and 4":
 *   1 · the QR card, MOVED here from My Info — a marketing asset was filed
 *       between the door code and the Wi-Fi password, which is why a real pro
 *       could not find it (the affordance-you-have-to-discover lesson).
 *   4 · "Make the most of your IMAGE Pro app" — coaching for the GlossGenius
 *       the pros already have. It PROMOTES the corporate tool on purpose:
 *       birthday drips and win-backs are in there unused, and teaching them
 *       is cheaper and more honest than rebuilding them.
 *
 * The Designer and Reviews arrive later behind their own feature keys — no
 * door ships to a thing that does not exist.
 *
 * GATED by the 'grow' switchboard key (useFeature): pilot one pro, one
 * studio, or everywhere with grant rows, never a deploy.
 */

type GrowTenant = {
  first_name: string | null;
  website_url: string | null;
  instagram_handle: string | null;
  business_instagram: string | null;
};

/** The IMAGE Pro app coaching shelf. Each guide says WHY it matters and what
 *  to set up, then hands off to GlossGenius's OWN how-to — their app, their
 *  docs. Every URL verified answering 200 on 2026-08-27; capability claims
 *  come from glossgenius.com/features/marketing, read the same day. */
export const PRO_APP_GUIDES: ReadonlyArray<{
  title: string;
  minutes: number;
  why: string;
  steps: string[];
  linkLabel: string;
  href: string;
}> = [
  {
    title: 'Put your birthday messages on autopilot',
    minutes: 4,
    why: 'A birthday text is the highest-response message you will send all year — and your IMAGE Pro app can send it for you, every year, without you remembering.',
    steps: [
      'Start collecting birthdays: ask at checkout, or add a birthday field to your booking questions.',
      'In the IMAGE Pro app, open Marketing and set up the birthday message with a small treat — a dollars-off add-on works better than a percent.',
      'Let it run. Check once a month that it is still on.',
    ],
    linkLabel: 'GlossGenius: email & text marketing FAQs',
    href: 'https://glossgenius.elevio.help/en/articles/144-email-text-marketing-faqs',
  },
  {
    title: 'Win back clients who went quiet',
    minutes: 5,
    why: 'Most "lost" clients did not leave — life happened. A short "miss you, here is my book" text to people you have not seen in 8+ weeks fills slow weeks from names you already have.',
    steps: [
      'In Marketing, build a text campaign and target it by visit history — clients you have not seen recently.',
      'Keep it to two sentences and one booking link. No guilt, no "where have you been".',
      'Send it on a Sunday evening — people plan their week then.',
    ],
    linkLabel: 'GlossGenius: create a text campaign',
    href: 'https://glossgenius.elevio.help/en/articles/781-how-to-create-a-text-marketing-campaign',
  },
  {
    title: 'Send your first email campaign',
    minutes: 6,
    why: 'Email is free and unlimited in your IMAGE Pro app, and it is the right channel for the longer story — a new service, fall openings, a price update.',
    steps: [
      'Open Marketing and start an email campaign — pick a template and swap in your words and a photo of your work.',
      'One topic per email. "Fall balayage openings" beats a newsletter.',
      'Send to everyone once a month at most — your list is worth more than any single send.',
    ],
    linkLabel: 'GlossGenius: create an email campaign',
    href: 'https://glossgenius.elevio.help/en/articles/136-how-to-create-an-email-marketing-campaign',
  },
  {
    title: 'Turn on text marketing the right way',
    minutes: 3,
    why: 'Clients are far more likely to book from a text than an email — but texting has rules (consent, opt-out) and the app handles them for you once it is set up properly.',
    steps: [
      'Read the short setup guide first — it covers consent, so you never message someone who did not ask.',
      'Then send your first campaign to a small group and see who books.',
    ],
    linkLabel: 'GlossGenius: getting started with text marketing',
    href: 'https://glossgenius.elevio.help/en/articles/1019-getting-started-with-text-marketing',
  },
];

function GuideCard({ g }: { g: (typeof PRO_APP_GUIDES)[number] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-image-border first:border-t-0">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 py-3 text-left"
      >
        <span className="text-sm font-semibold text-image-black">{g.title}</span>
        <span className="shrink-0 text-[10px] text-image-charcoal">{g.minutes} min</span>
      </button>
      {open && (
        <div className="pb-4 text-sm">
          <p className="text-image-dark-charcoal">{g.why}</p>
          <ol className="mt-2 space-y-1.5 list-decimal list-inside text-image-dark-charcoal">
            {g.steps.map(s => <li key={s}>{s}</li>)}
          </ol>
          <a
            href={g.href}
            target="_blank"
            rel="noreferrer"
            className="inline-block mt-3 text-xs font-bold text-image-pink hover:underline"
          >
            {g.linkLabel}
          </a>
        </div>
      )}
    </div>
  );
}

/**
 * YOUR MARKET (Pro Intelligence Phase 1 — WI e7c1f29d, Peter's green light 2026-08-28).
 *
 * Evolved twice on Peter's walk, same night: first "The grow Your market is not about
 * waxing" (a hairstylist must never be told waxing is their market), then "it makes
 * sense to see everything that has filters/chips by profession — it's the same concepts
 * we use in the owner's area with content insights." So: the feed shows every tracked
 * market, filtered by single-select profession chips (the Content Insights idiom), with
 * the pro's OWN craft pre-selected when we know it — their craft is the default lens,
 * never a wall, since it is all public Instagram content.
 *
 * The chip row is DATA (public.pro_market_markets — niche, region, count, is_yours),
 * served by the same database that serves the feed, so the chips can never disagree
 * with what the feed can return. No market name is ever hardcoded here.
 */
type Market = { niche: string; region: string | null; artists: number; is_yours: boolean };

/**
 * HOW YOU'RE DOING (PRO-INSIGHTS-1 — Peter: "mirror it to the owner's surface and call
 * it Insights, with the users instagram stats" → "go, build it"). The pro's own
 * Instagram, read nightly through the same official tool as the market sweep, against
 * the handle on their own record. Sits ABOVE the market feed on purpose: their own
 * numbers are the reason to come back weekly; the market is the homework.
 *
 * Every state is honest: no handle → point at My Info (where the field really lives and
 * is editable); Instagram refused → say why (personal accounts share no numbers) and how
 * to fix it; not swept yet → name the schedule. Instagram's own answer is the verdict —
 * we never guess whose account a handle is, we show which handle we read.
 */
type IgInsights = {
  username: string; followers: number | null; media_count: number | null;
  profile_error: string | null; last_swept_at: string | null;
  prev_followers: number | null; prev_captured_at: string | null;
  avg_engagement: number | null; niche: string | null; market_avg: number | null;
};
type IgPost = {
  external_id: string; media_type: string | null; permalink: string | null;
  caption: string | null; poster_r2_key: string | null; taken_at: string | null;
  likes: number; comments: number;
};

function HowYoureDoing({ locationId }: { locationId: string | null }) {
  const qc = useQueryClient();
  const [updating, setUpdating] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const { data: ins, isLoading } = useQuery({
    queryKey: ['proIgInsights', locationId],
    enabled: !!locationId,
    queryFn: async () => {
      if (isCell()) {
        // CELL (M28): GET /v1/pro-hub/locations/{locationId}/ig/insights — the same rpc, the same rows[0].
        const { data, error } = await cellGet<{ rows: IgInsights[] }>(`/v1/pro-hub/locations/${encodeURIComponent(locationId!)}/ig/insights`, viewQuery());
        if (error) throw error;
        return ((data?.rows ?? [])[0] ?? null) as IgInsights | null;
      }
      const { data: rows, error } = await supabase.rpc('pro_ig_insights', { p_location: locationId!, ...viewRpcArgs() });
      if (error) throw error;
      return ((rows ?? [])[0] ?? null) as IgInsights | null;
    },
  });
  const ok = !!ins && !ins.profile_error && ins.last_swept_at != null && ins.followers != null;
  const { data: posts } = useQuery({
    queryKey: ['proIgPosts', locationId],
    enabled: !!locationId && ok,
    queryFn: async () => {
      if (isCell()) {
        // CELL (M28): GET /v1/pro-hub/locations/{locationId}/ig/posts?limit=6 — the same rpc.
        const { data, error } = await cellGet<{ rows: IgPost[] }>(`/v1/pro-hub/locations/${encodeURIComponent(locationId!)}/ig/posts`, { limit: '6', ...viewQuery() });
        if (error) throw error;
        return (data?.rows ?? []) as IgPost[];
      }
      const { data: rows, error } = await supabase.rpc('pro_ig_posts', { p_location: locationId!, p_limit: 6, ...viewRpcArgs() });
      if (error) throw error;
      return (rows ?? []) as IgPost[];
    },
  });
  if (!locationId || isLoading) return null;
  // THE UPDATE BUTTON (Peter: "instead of a static section, they can update it so that
  // we are not running nightly scan to keep it all updated"). The door rate-limits
  // server-side; this posts, relays the door's own sentence, and refetches.
  const refresh = async () => {
    if (viewAsProId()) { setNote('View-as is read only — ask the pro to press Update, or exit view-as.'); return; }
    setUpdating(true);
    setNote(null);
    try {
      // CELL (M28): token from lib/cell/ownerHub.ts (production: the Supabase token; the cell build: Cognito's ID token).
      if (!OWNER_HUB_ORIGIN) { setNote(CELL_DOOR_OFF); return; }
      const at = await ownerHubToken();
      if (!at) { setNote('Sign in again to update.'); return; }
      const r = await fetch(`${OWNER_HUB_ORIGIN}/pro/ig-refresh`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${at}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ location_id: locationId }),
      });
      const j = (await r.json().catch(() => ({}))) as { ok?: boolean; state?: string; error?: string };
      if (!r.ok) { setNote(j.error ?? 'That did not work — try again.'); return; }
      if (j.error) setNote(j.error);
      await qc.invalidateQueries({ queryKey: ['proIgInsights', locationId] });
      await qc.invalidateQueries({ queryKey: ['proIgPosts', locationId] });
    } catch {
      setNote('That did not work — try again.');
    } finally {
      setUpdating(false);
    }
  };
  const updateBtn = (
    <button type="button" onClick={refresh} disabled={updating}
      className="rounded-full px-3 py-1 text-xs font-bold ring-1 ring-image-pink text-image-pink hover:bg-image-pink hover:text-white transition-all disabled:opacity-50">
      {updating ? 'Updating…' : 'Update'}
    </button>
  );
  const delta = ok && ins!.prev_followers != null ? (ins!.followers ?? 0) - ins!.prev_followers : null;
  const since = ins?.prev_captured_at
    ? new Date(ins.prev_captured_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : null;
  return (
    <section className="bg-white ring-1 ring-image-border rounded-2xl p-5" data-how-youre-doing>
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-bold tracking-tight text-image-black">How you&rsquo;re doing</h2>
        {updateBtn}
      </div>
      {note && <p className="text-xs text-image-charcoal mt-1">{note}</p>}
      {!ins ? (
        <p className="text-sm text-image-charcoal mt-2">
          Your own Instagram numbers appear here — followers, your posts ranked by engagement, and
          how you compare to your profession.{' '}
          <Link to="/my-info" className="font-bold text-image-pink hover:underline">
            Add your Instagram in My Info
          </Link>
          , then press Update.
        </p>
      ) : ins.profile_error ? (
        <p className="text-sm text-image-charcoal mt-2">
          We couldn&rsquo;t read <span className="font-bold">@{ins.username}</span>. Instagram only
          shares numbers for Business and Creator accounts — switching is free, in the Instagram
          app under Settings. If that isn&rsquo;t your account,{' '}
          <Link to="/my-info" className="font-bold text-image-pink hover:underline">
            fix the handle in My Info
          </Link>.
        </p>
      ) : !ok ? (
        <p className="text-sm text-image-charcoal mt-2">
          <span className="font-bold">@{ins.username}</span> is set — press Update to pull your
          numbers.
        </p>
      ) : (
        <>
          {/* design/financy · THE PRO'S OWN NUMBERS GET THE HERO. Three equal figures in a white
              card said everything and emphasised nothing; followers is the number a pro's book
              grows by, so it leads, and posts and engagement become blocks in the colours those
              two facts wear everywhere else — blue for reach, green for a thing earned. Nothing is
              dropped: the handle, the as-of date, the since-delta and the profession's average are
              all still here. */}
          <div className="fin-hero fin-hero-tight">
            <p className="finkick">
              @{ins.username}
              {ins.last_swept_at ? ` · as of ${new Date(ins.last_swept_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}` : ''}
            </p>
            <p className="finhead">
              <span className="finbig">{(ins.followers ?? 0).toLocaleString()}</span>
              <span className="finof">
                followers
                {delta != null && since ? ` · ${delta >= 0 ? '+' : ''}${delta} since ${since}` : ''}
              </span>
            </p>
          </div>
          <div className="fin-blocks two" style={{ marginTop: 12 }}>
            <div className="fin-blk blue">
              <span className="ic" aria-hidden="true">{'\u25A4'}</span>
              <span>
                <span className="v">{(ins.media_count ?? 0).toLocaleString()}</span>
                <span className="k">Posts</span>
              </span>
            </div>
            <div className="fin-blk green">
              <span className="ic" aria-hidden="true">{'\u2665'}</span>
              <span>
                <span className="v">{ins.avg_engagement != null ? Number(ins.avg_engagement).toLocaleString() : '—'}</span>
                <span className="k">Avg engagement</span>
                {ins.market_avg != null && ins.niche && (
                  <span className="s">{ins.niche} average {Number(ins.market_avg).toLocaleString()}</span>
                )}
              </span>
            </div>
          </div>
          <MoreInsight locationId={locationId} />
          {!!posts?.length && (
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-4" data-own-posts>
              {posts.map((p) => (
                <a key={p.external_id} href={p.permalink ?? undefined} target="_blank" rel="noreferrer"
                  className="block rounded-lg overflow-hidden ring-1 ring-image-border hover:ring-image-pink transition-all">
                  {p.poster_r2_key ? (
                    <img src={mediaUrl(p.poster_r2_key)} alt="" loading="lazy"
                      className="w-full aspect-square object-cover" />
                  ) : (
                    <div className="w-full aspect-square bg-image-cream" />
                  )}
                  <div className="px-1.5 py-1 text-[10px] text-image-charcoal">❤ {p.likes.toLocaleString()}</div>
                </a>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}

/**
 * WHAT TO DO NEXT (PRO-SCAN-1 — Peter: "as an expert marketing agent who is familiar
 * with the health and wellness industry, analyze this... only feedback, he kinda
 * suggests to them... a recommendation engine is what I'm thinking").
 *
 * One button, one metered call, suggestions only — each carrying its numbers. Served by
 * the /pro/ig-scan door, which caches until the pro's next Update: pressing Analyze on
 * unchanged data returns the same answer for free. The bio draft (their own posts, their
 * voice) rides along. In view-as the door refuses on the viewer's own token; the card
 * says so instead of pretending.
 */
type Scan = {
  id: string;
  recommendations: Array<{ title: string; why: string; evidence: string[]; make_brief: string }>;
  bio_draft: string | null;
  created_at: string;
};

function WhatToDoNext({ locationId }: { locationId: string | null }) {
  const qc = useQueryClient();
  const [running, setRunning] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ['proIgScan', locationId],
    enabled: !!locationId,
    queryFn: async () => {
      // CELL (M28): token from lib/cell/ownerHub.ts (production: the Supabase token; the cell build: Cognito's ID token).
      if (!OWNER_HUB_ORIGIN) { setNote(CELL_DOOR_OFF); return null; }
      const at = await ownerHubToken();
      if (!at) return null;
      const r = await fetch(`${OWNER_HUB_ORIGIN}/pro/ig-scan`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${at}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ location_id: locationId, op: 'read', view_pro: viewAsProId() ?? undefined }),
      });
      const j = (await r.json().catch(() => null)) as { ok?: boolean; scan?: Scan | null } | null;
      return j?.scan ?? null;
    },
  });
  if (!locationId) return null;
  const analyze = async () => {
    if (viewAsProId()) { setNote('View-as is read only — the pro runs their own analysis.'); return; }
    setRunning(true);
    setNote(null);
    try {
      // CELL (M28): token from lib/cell/ownerHub.ts (production: the Supabase token; the cell build: Cognito's ID token).
      if (!OWNER_HUB_ORIGIN) { setNote(CELL_DOOR_OFF); return; }
      const at = await ownerHubToken();
      if (!at) { setNote('Sign in again to analyze.'); return; }
      const r = await fetch(`${OWNER_HUB_ORIGIN}/pro/ig-scan`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${at}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ location_id: locationId, op: 'scan' }),
      });
      const j = (await r.json().catch(() => ({}))) as { ok?: boolean; state?: string; error?: string };
      if (!r.ok || j.error) { setNote(j.error ?? 'The analysis is unavailable right now — try again in a few minutes.'); return; }
      if (j.state === 'cached') setNote('Your posts haven’t changed since this analysis — press Update first to pull fresh numbers.');
      if (j.state === 'cooldown' && j.error) setNote(j.error); // the unlock date, in the door's own words
      await qc.invalidateQueries({ queryKey: ['proIgScan', locationId] });
    } catch {
      setNote('The analysis is unavailable right now — try again in a few minutes.');
    } finally {
      setRunning(false);
    }
  };
  return (
    <section className="bg-white ring-1 ring-image-border rounded-2xl p-5 border-l-4 border-l-image-pink" data-what-to-do-next>
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-bold tracking-tight text-image-black">What to do next</h2>
        <button type="button" onClick={analyze} disabled={running}
          className="rounded-full px-3 py-1 text-xs font-bold bg-image-pink text-white hover:opacity-90 transition-all disabled:opacity-50">
          {running ? 'Reading your posts…' : 'Analyze my Instagram'}
        </button>
      </div>
      {note && <p className="text-xs text-image-charcoal mt-1">{note}</p>}
      {running && (
        <p className="text-sm text-image-charcoal mt-2">
          Reading your posts and your market — this takes about twenty seconds.
        </p>
      )}
      {!running && !data && !isLoading && (
        <p className="text-sm text-image-charcoal mt-2">
          An expert read of your Instagram — what’s working, what your market’s winners do
          that you don’t yet, and a bio drafted from your own posts. Suggestions only; every one
          carries its numbers.
        </p>
      )}
      {!running && data && (
        <div className="mt-3 space-y-3">
          {data.recommendations.map((rec, i) => (
            <div key={i} className="rounded-xl bg-image-cream/60 p-3">
              <div className="text-sm font-bold text-image-black">{rec.title}</div>
              <p className="text-xs text-image-charcoal mt-1">{rec.why}</p>
              {rec.evidence.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-1.5">
                  {rec.evidence.map((e, k) => (
                    <a key={k} href={e} target="_blank" rel="noreferrer"
                      className="text-[10px] font-bold text-image-pink hover:underline">
                      see the post
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
          {data.bio_draft && (
            <div className="rounded-xl ring-1 ring-image-border p-3">
              <div className="text-[10px] font-extrabold tracking-[0.12em] uppercase text-image-pink">
                A bio, drafted from your posts
              </div>
              <p className="text-sm text-image-dark-charcoal mt-1 select-all">{data.bio_draft}</p>
              <p className="text-[10px] text-image-charcoal mt-1">
                Yours to use — paste it into Instagram if you like it.
              </p>
            </div>
          )}
          <p className="text-[10px] text-image-charcoal">
            As of {new Date(data.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} ·
            suggestions from your own numbers and your market — what you post is always your call.
          </p>
        </div>
      )}
    </section>
  );
}

/**
 * MORE INSIGHT — the Instagram connect opt-in, folded into the data bar (Peter,
 * 2026-08-29: "Go deeper need to be more connected to the analysis section up top...
 * button on the data bar that say 'More insight'... click and get the connect button").
 * The pitch lives where the numbers live; the hand-raise is recorded server-side and
 * the copy never claims a connection exists yet (guard-pinned).
 */
function MoreInsight({ locationId }: { locationId: string | null }) {
  const [open, setOpen] = useState(false);
  // HUB-065 (Tenean review 2026-10-06): "on the list" is a fact about the PRO, asked of the door for
  // the session's own pro — never a per-browser flag. The old localStorage 'hub-ig-oauth-interest'
  // told pro B they were registered on a browser pro A had used, and hid the button they needed.
  // null = not asked yet / unknown; the fold asks the moment it opens.
  const [noted, setNoted] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const ask = async (body: { op: 'connect_interest_status' } | { op: 'connect_interest' }): Promise<{ r: Response | null; j: { ok?: boolean; state?: string; error?: string } }> => {
    // CELL (M28): token from lib/cell/ownerHub.ts (production: the Supabase token; the cell build: Cognito's ID token).
    if (!OWNER_HUB_ORIGIN) return { r: null, j: { error: CELL_DOOR_OFF } };
    const at = await ownerHubToken();
    if (!at || !locationId) return { r: null, j: {} };
    const r = await fetch(`${OWNER_HUB_ORIGIN}/pro/ig-refresh`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${at}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ location_id: locationId, ...body }),
    });
    const j = (await r.json().catch(() => ({}))) as { ok?: boolean; state?: string; error?: string };
    return { r, j };
  };
  useEffect(() => {
    if (!open || noted !== null || !locationId) return;
    let live = true;
    ask({ op: 'connect_interest_status' })
      .then(({ r, j }) => { if (live && r?.ok) setNoted(j.state === 'noted'); })
      .catch(() => { /* unknown stays unknown: the button is offered, and the door answers on press */ });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, locationId]);
  if (!locationId) return null;
  const raise = async () => {
    if (viewAsProId()) return;
    setBusy(true);
    setSaid(null);
    try {
      const { r, j } = await ask({ op: 'connect_interest' });
      if (!r) { setSaid(j.error ?? 'Sign in again to raise your hand.'); return; }
      if (!r.ok || j.state !== 'noted') { setSaid(j.error ?? 'That did not go through — try again.'); return; }
      setNoted(true);
    } catch {
      setSaid('That did not go through — try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-3" data-more-insight>
      <button type="button" aria-expanded={open} onClick={() => setOpen(o => !o)}
        className="rounded-full px-3 py-1 text-xs font-bold ring-1 ring-image-border text-image-charcoal hover:ring-image-pink hover:text-image-pink transition-all">
        More insight
      </button>
      {open && (
        <div className="mt-2 rounded-xl bg-image-cream/60 p-3">
          <p className="text-xs text-image-charcoal">
            These numbers are what Instagram shows publicly. Connect your Instagram (free, one tap
            when it opens) and your analysis learns what likes can&rsquo;t show: how many people
            your posts actually reach, who saves them, who your audience is, and when they&rsquo;re
            online — so your recommendations come from your own audience&rsquo;s behavior.
          </p>
          <div className="mt-2">
            {noted === true ? (
              <span className="text-[10px] font-extrabold tracking-[0.12em] uppercase bg-white ring-1 ring-image-border text-image-charcoal rounded-full px-2 py-0.5">
                You&rsquo;re on the list — first when it switches on
              </span>
            ) : (
              <button type="button" onClick={raise} disabled={busy}
                className="rounded-full px-3 py-1 text-xs font-bold ring-1 ring-image-pink text-image-pink hover:bg-image-pink hover:text-white transition-all disabled:opacity-50">
                {busy ? 'One sec…' : 'Count me in'}
              </button>
            )}
            {said && <p className="mt-1 text-xs text-image-charcoal" role="status">{said}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

type MarketPost = {
  username: string; artist: string; town: string | null; caption: string | null;
  permalink: string | null; media_key: string | null; kind: string;
  taken_at: string | null; engagement: number | null; likes: number | null;
  comments: number | null; followers: number | null;
};

function MarketFeed({ locationId }: { locationId: string | null }) {
  const { data: markets, isLoading: marketsLoading } = useQuery({
    queryKey: ['proMarketMarkets', locationId],
    enabled: !!locationId,
    queryFn: async () => {
      if (isCell()) {
        // CELL (M28): GET /v1/pro-hub/locations/{locationId}/market/markets — the same rpc.
        const { data, error } = await cellGet<{ rows: Market[] }>(`/v1/pro-hub/locations/${encodeURIComponent(locationId!)}/market/markets`, viewQuery());
        if (error) throw error;
        return (data?.rows ?? []) as Market[];
      }
      const { data: rows, error } = await supabase.rpc('pro_market_markets', { p_location: locationId!, ...viewRpcArgs() });
      if (error) throw error;
      return (rows ?? []) as Market[];
    },
  });
  // 'all' = the Everything chip; null = no explicit pick yet, so the pro's own craft
  // (is_yours) leads when tracked, Everything otherwise.
  const [picked, setPicked] = useState<string | null>(null);
  const yours = markets?.find((m) => m.is_yours)?.niche ?? null;
  const selected = picked === 'all' ? null : (picked ?? yours);
  const market = selected ? markets?.find((m) => m.niche === selected) ?? null : null;
  const { data, isLoading } = useQuery({
    queryKey: ['proMarketFeed', locationId, selected],
    enabled: !!locationId && !!markets?.length,
    queryFn: async () => {
      if (isCell()) {
        // CELL (M28): GET /v1/pro-hub/locations/{locationId}/market/feed?limit=30&niche= — the same rpc, and (as on
        // Supabase) NO view-as argument: pro_market_feed takes none.
        const { data, error } = await cellGet<{ rows: MarketPost[] }>(`/v1/pro-hub/locations/${encodeURIComponent(locationId!)}/market/feed`, { limit: '30', niche: selected });
        if (error) throw error;
        return (data?.rows ?? []) as MarketPost[];
      }
      const { data: rows, error } = await supabase.rpc('pro_market_feed', {
        p_location: locationId!, p_limit: 30, p_niche: selected,
      });
      if (error) throw error;
      return (rows ?? []) as MarketPost[];
    },
  });
  if (!locationId || marketsLoading) return null;
  // No tracked markets at all: either the gates refused (the room-level fence already
  // spoke) or the corpus has not opened its first market yet — say the honest thing.
  if (!markets?.length) {
    return (
      <section className="bg-white ring-1 ring-image-border rounded-2xl p-5">
        <h2 className="font-bold tracking-tight text-image-black">Your market</h2>
        <p className="text-sm text-image-charcoal mt-2">
          This is where you&rsquo;ll see what artists in tracked markets are posting, ranked by
          what their audiences actually engage with. The first markets are being built now.
        </p>
      </section>
    );
  }
  const chip = (active: boolean) =>
    `rounded-full px-3 py-1 text-xs font-semibold ring-1 transition-all ${
      active
        ? 'bg-image-pink text-white ring-image-pink'
        : 'bg-white text-image-charcoal ring-image-border hover:ring-image-pink'
    }`;
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  return (
    <section className="bg-white ring-1 ring-image-border rounded-2xl p-5">
      <h2 className="font-bold tracking-tight text-image-black">Your market</h2>
      <p className="text-xs text-image-charcoal mt-0.5">
        Real posts from working artists, ranked by what their audiences actually engage with.
        Public posts, collected through Instagram&rsquo;s official tools, refreshed nightly.
        Tap a post to see it on Instagram.
      </p>
      {/* The profession chips — the owner room's Content Insights idiom (Peter: "pro
          section should follow this model, the filter is professions"): single-select,
          an All option, counts from the same source the feed reads. */}
      <div className="flex flex-wrap gap-2 mt-3" data-market-chips>
        <button type="button" aria-pressed={selected === null} className={chip(selected === null)}
          onClick={() => setPicked('all')}>
          All
        </button>
        {markets.map((m) => (
          <button key={m.niche} type="button" aria-pressed={selected === m.niche}
            className={chip(selected === m.niche)} onClick={() => setPicked(m.niche)}>
            {cap(m.niche)} · {m.artists}{m.is_yours ? ' · yours' : ''}
          </button>
        ))}
      </div>
      {isLoading ? (
        <p className="text-sm text-image-charcoal mt-4">Reading the market&hellip;</p>
      ) : !data?.length ? (
        <p className="text-sm text-image-charcoal mt-4">
          The first collection run is tonight — this room fills by itself and stays fresh nightly.
          {market ? ` ${market.artists} artists are being tracked for you already.` : ''}
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4" data-market-feed>
          {/* The card follows the Insights model: media with a play badge for reels,
              handle · business, the caption, likes and comments split, the town as the
              tag chip. No "Make my version" — the pro Designer is not live yet, and a
              door ships only when the thing behind it exists. */}
          {data.map((r) => (
            <a key={`${r.username}-${r.permalink}`} href={r.permalink ?? undefined}
              target="_blank" rel="noreferrer"
              className="block bg-white ring-1 ring-image-border rounded-xl overflow-hidden hover:ring-image-pink transition-all">
              <div className="relative">
                {r.media_key ? (
                  <img src={mediaUrl(r.media_key)} alt="" loading="lazy"
                    className="w-full aspect-square object-cover" />
                ) : (
                  <div className="w-full aspect-square bg-image-cream" />
                )}
                {r.kind === 'reel' && (
                  <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/55 text-white text-[10px] flex items-center justify-center">
                    ▶
                  </span>
                )}
              </div>
              <div className="p-3">
                <div className="text-xs truncate">
                  <span className="font-bold text-image-black">@{r.username}</span>
                  {r.artist && r.artist !== r.username && (
                    <span className="text-image-charcoal"> · {r.artist}</span>
                  )}
                </div>
                {r.caption && (
                  <p className="text-xs text-image-charcoal mt-1 line-clamp-3">{r.caption}</p>
                )}
                <div className="flex items-center gap-2 mt-2 text-[11px] text-image-charcoal">
                  {r.likes != null && <span>❤ {r.likes.toLocaleString()}</span>}
                  {r.comments != null && <span>💬 {r.comments.toLocaleString()}</span>}
                  {r.town && (
                    <span className="ml-auto bg-image-cream rounded-full px-2 py-0.5 text-[10px] truncate max-w-[45%]">
                      {r.town}
                    </span>
                  )}
                </div>
              </div>
            </a>
          ))}
        </div>
      )}
    </section>
  );
}

export default function Grow() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const { activeLocationId } = useActiveLocationId();
  const grow = useFeature('grow');
  const hasSpotlight = useHasSpotlight();
  // THE TABS (Peter, 2026-08-29: "this list is scrolling too much" -> the owner room's
  // section-tab idiom, order his call after asking for pushback: Insights leads because
  // their own numbers are the pull; Create closes the narrative (see your numbers, see
  // the market, make something) and every "Make it" will deep-link into it anyway.
  // URL-addressed (?tab=) like the owner's My Studio tabs, so a tab is a shareable place.
  const [params, setParams] = useSearchParams();
  // Reviews' tile comes alive with its own switchboard key — the tile and the
  // page read the SAME gate, so a live tile can never open a refused room.
  const reviews = useFeature('reviews');
  const signature = useFeature('signature');
  const designer = useFeature('designer');
  const reviewsOn = reviews.enabled;
  const signatureOn = signature.enabled;
  const designerOn = designer.enabled;
  // EVERY TAB IS ITS OWN SWITCH (Peter, 2026-09-07 — decision f9dee6b1). Before
  // tonight only Signature had a key, so Insights, Check my listing and Your
  // market could not be turned off at all. His model, verbatim: "growth room on,
  // Insights could be on, signature could be off, check my listing could be off,
  // markets could be off... each individual tab on the grow needs to have its own
  // checkbox." The QR code carries its own key because it is printed on the pro's
  // DOOR SIGN — holding the Designer back must never orphan a code nobody can
  // repoint — so Create appears when EITHER of its two switches is on.
  const insights = useFeature('insights');
  const listing = useFeature('listing');
  const market = useFeature('market');
  const qr = useFeature('qr');
  const insightsOn = insights.enabled;
  const listingOn = listing.enabled;
  const marketOn = market.enabled;
  const qrOn = qr.enabled;
  const createOn = designerOn || qrOn;
  // Hidden-while-loading is this hook's contract on purpose ("a door that appears
  // is fine, one that vanishes after a beat reads as the app taking something
  // away"), which is right for a TAB. It is wrong for the empty state below,
  // because that one makes a CLAIM — "your studio has not switched anything on" —
  // and every switch reads false for the first beat. So the claim waits until the
  // switchboard has actually answered.
  const switchesLoading = insights.loading || listing.loading || market.loading
    || qr.loading || signature.loading || designer.loading;

  // A SWITCHED-OFF SECTION LEAVES NO TRACE (his call the same night). It is not a
  // greyed tab and not a "Coming" tile: a tile naming a feature he deliberately
  // held back advertises it to every pro and manufactures the demand the staged
  // rollout exists to measure. Absence, not a promise.
  const OPEN: ReadonlyArray<readonly [string, string]> = [
    ...(insightsOn ? [['insights', 'Insights'] as const] : []),
    ...(signatureOn ? [['signature', 'Signature'] as const] : []),
    ...(listingOn ? [['listing', 'Check my listing'] as const] : []),
    ...(marketOn ? [['market', 'Your market'] as const] : []),
    ...(createOn ? [['create', 'Create'] as const] : []),
  ];
  // The address only resolves to a tab that is actually open, so a bookmark or a
  // deep link to a section since switched off lands on the first real one rather
  // than an empty room that looks broken.
  const asked = params.get('tab') ?? '';
  const tab = OPEN.some(([k]) => k === asked) ? asked : (OPEN[0]?.[0] ?? '');

  const { data, isLoading } = useQuery({
    queryKey: ['grow', userId, activeLocationId],
    enabled: !!userId && grow.enabled,
    queryFn: async () => {
      if (isCell()) {
        // CELL (M28): GET /v1/pro-hub/users/{authUserId}/grow?location= — the pro, then their tenancy (by studio, or
        // the newest), in one call; a tenant read error is ignored as below.
        const { data, error } = await cellGet<{ pro: { id: string; first_name: string | null } | null; tenant: GrowTenant | null }>(
          `/v1/pro-hub/users/${encodeURIComponent(userId!)}/grow`, { location: activeLocationId });
        if (error) throw error;
        if (!data?.pro) return null;
        return { pro: data.pro, tenant: data.tenant ?? null };
      }
      const { data: pro, error: proErr } = await supabase
        .from('pros')
        .select('id, first_name')
        .eq('auth_user_id', userId!)
        .maybeSingle();
      if (proErr) throw proErr;
      if (!pro) return null;
      // CELL (M28): this second read is answered by the same /grow call in the cell branch above.
      const tenantQuery = supabase
        .from('tenants')
        .select('first_name, website_url, instagram_handle, business_instagram')
        .eq('pro_id', pro.id);
      const { data: tenant } = activeLocationId
        ? await tenantQuery.eq('location_id', activeLocationId).maybeSingle<GrowTenant>()
        : await tenantQuery.order('updated_at', { ascending: false }).limit(1).maybeSingle<GrowTenant>();
      return { pro, tenant: tenant ?? null };
    },
  });

  // The fence: a direct URL to a dark feature says so honestly — the same
  // posture as the Owner Hub's refused doors, in the pro's own language.
  if (!grow.loading && !grow.enabled) {
    return (
      <div className="card text-sm text-image-charcoal">
        This section isn&rsquo;t switched on for your studio yet.
      </div>
    );
  }

  if (grow.loading || isLoading) {
    return <div className="text-image-slate text-sm">Loading…</div>;
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-2xl font-bold text-image-black">
          <span className="text-image-pink">Grow</span>
        </h1>
        <p className="text-sm text-image-charcoal mt-1">
          Tools for building your book. Your studio provides them — your business is yours.
        </p>
      </div>

      {/* The shared wing row (PROHUB-43) — same treatment as Studio's, one component,
          so the two wings' top menus cannot drift apart. */}
      {/* A PEER, NOT A TILE (Peter's IA call the day Signature shipped: a tile meant
          "click another thing and click back out"). The row is now DERIVED from the
          switches, so a tab and its content can never disagree about whether a
          section exists — and a tab to a refused room, a door painted on a wall,
          is structurally impossible. */}
      {OPEN.length > 1 && (
        <div data-grow-tabs>
          <WingTabs
            tabs={OPEN}
            active={tab}
            onPick={(k) => setParams(k === OPEN[0][0] ? {} : { tab: k }, { replace: true })}
          />
        </div>
      )}

      {/* The room is on but every section inside it is off. Say so plainly rather
          than render a header over nothing, which reads as broken rather than as
          a deliberate setting. */}
      {OPEN.length === 0 && !switchesLoading && (
        <div className="card">
          <p className="text-sm text-image-charcoal">
            Nothing is switched on in here yet. Your studio will let you know when there is.
          </p>
        </div>
      )}

      {tab === 'insights' && (
        <>
          <HowYoureDoing locationId={activeLocationId} />
          <WhatToDoNext locationId={activeLocationId} />
          {reviewsOn ? (
            <Link to="/reviews" className="card block relative hover:ring-image-pink transition-all">
              <span className="absolute top-3 right-3 text-[9px] font-extrabold tracking-[0.12em] uppercase bg-image-pink text-white rounded-full px-2 py-0.5">
                Live
              </span>
              <div className="font-bold tracking-tight text-image-black">Reviews</div>
              <div className="text-xs mt-0.5 text-image-charcoal">
                Your own Google reviews — part of how you&rsquo;re doing
              </div>
            </Link>
          ) : null}
        </>
      )}

      {tab === 'signature' && signatureOn && <Signature />}

      {tab === 'listing' && <CheckMyListing />}

      {tab === 'market' && <MarketFeed locationId={activeLocationId} />}

      {tab === 'create' && (
        <>
          {/* The Designer leads the making tab; the tab is named Create because a tab
              called Designer opening to a card called The Designer is a stutter
              (Peter's catch). Behind its own switchboard key so Peter flips it per
              studio and per pro — Erica and a couple of Sharon pros validate first
              (decision 0d165af7). OFF NOW RENDERS NOTHING, not a COMING tile
              (decision f9dee6b1): a tile naming a held-back feature advertises it
              to every pro. The old "Content" roadmap tile is GONE: it became the
              Your market tab. */}
          {designerOn && (
            <>
              <ProDesigner locationId={activeLocationId} />
              <ProLibrary locationId={activeLocationId} />
            </>
          )}
          {qrOn && data?.pro && (
            <QrCodeCard
              proId={data.pro.id}
              firstName={data.tenant?.first_name ?? data.pro.first_name}
              websiteUrl={data.tenant?.website_url ?? null}
              instagram={data.tenant?.instagram_handle ?? null}
              businessInstagram={data.tenant?.business_instagram ?? null}
            />
          )}
          <div className="card">
            <div className="text-[10px] font-extrabold tracking-[0.14em] text-image-pink uppercase">
              Your IMAGE Pro app
            </div>
            <h2 className="font-display font-extrabold text-lg mt-0.5">Use what you already have.</h2>
            <p className="text-xs text-image-charcoal mt-1 mb-2">
              Your IMAGE Pro app can already do more than most pros ever switch on. Each of these
              takes a few minutes, once.
            </p>
            {PRO_APP_GUIDES.map(g => <GuideCard key={g.title} g={g} />)}
          </div>
        </>
      )}

      {/* SPOTLIGHT IS INVISIBLE UNTIL THE OWNER SENDS ONE (Peter, 2026-08-20)
          — the same gate the old More sheet used. Being featured is a growth
          moment, so the row lives in this wing once it exists. */}
      {hasSpotlight && (
        <Link to="/spotlight" className="card flex items-center justify-between gap-3 hover:ring-image-pink transition-all">
          <div>
            <div className="text-[10px] font-extrabold tracking-[0.14em] text-image-pink uppercase">
              Your spotlight
            </div>
            <div className="font-display font-extrabold text-lg mt-0.5">Pro Spotlight</div>
            <div className="text-xs text-image-charcoal">Your studio is featuring you — see where it stands.</div>
          </div>
        </Link>
      )}
    </div>
  );
}
